import {match} from 'ts-pattern';

import {isNotNil, omit} from 'ramda';

import type {CarFeaturesResponseBody} from '@dms/api/core';
import {VehicleTypeEnumObject} from '@dms/api/shared';
import type {
  ColorRequestBody,
  ConditionEnum,
  GetVehicleApiResponse,
  UpdateVehicleRequestBody,
  VehicleFeatureV2ResponseBody,
  VehicleTypeEnum,
} from '@dms/api/vehicle';

import {toApiModelFamilyFields} from 'features/vehicle-catalogue';

import {isElectricOrHybridFuelType} from '../../kit/utils/isElectricOrHybridFuelType';
import {parseSeatCount} from '../../kit/utils/parseSeatCount';
import type {BaseVehicle} from '../types/BaseVehicle';
import type {SaleVehicle} from '../types/SaleVehicle';
import {createCustomFields} from '../utils/createCustomFields';
import {getMakeValue} from '../utils/getMakeValue';

/**
 * Helper function to format color data for the API from either a string or a color object.
 */
const formatColor = (
  color: BaseVehicle['exteriorColor'] | BaseVehicle['interiorColor']
): ColorRequestBody | null => {
  if (!color) {
    return null;
  }

  // If the form sends a simple string, convert it to the object format the API expects
  if (typeof color === 'string') {
    return {value: color, name: color, code: color};
  }

  // If it's already an object, pass it through
  return color;
};

// Non-bus types expose only the 1–9 seat chips (options come from catalogueApi.seatCountOptions);
// a larger value can only be a leftover from the bus numeric input after a type switch.
const MAX_NON_BUS_SEAT_COUNT = 9;

// Only buses use the free numeric seat count. The value is kept in form state across type switches,
// so an out-of-range value can linger after switching away from bus — don't submit it for a non-bus
// type (its chips show nothing selected for it anyway).
const resolveApiSeatCount = (
  type: VehicleTypeEnum | undefined,
  rawSeatCount: string | number | undefined
): number | null => {
  const seatCount = parseSeatCount(rawSeatCount);
  if (seatCount === null || !Number.isInteger(seatCount) || seatCount <= 0) {
    return null;
  }
  if (type !== 'VEHICLETYPE_BUS' && seatCount > MAX_NON_BUS_SEAT_COUNT) {
    return null;
  }
  return seatCount;
};

/**
 * Helper function to determine feature highlighting and premium status
 */
const mapFeatures = (
  features: string[] | undefined,
  existingFeatures?: VehicleFeatureV2ResponseBody[] | null,
  sourcingData?: CarFeaturesResponseBody | null
) => {
  if (!features) {
    return [];
  }

  return features.map((key: string) => {
    // Check for existing feature with override highlighting (like in RentalVehicleForm)
    const existingFeature = existingFeatures?.find((feature) => feature.key === key);

    // Determine highlighting: use existing override or sourcing data
    const isHighlighted = existingFeature
      ? existingFeature.isHighlighted
      : (sourcingData?.highlightedFeatures?.includes(key) ?? false);

    // Determine premium status from sourcing data
    const isPremium = sourcingData?.premiumFeatures?.includes(key) ?? false;

    return {
      key,
      isHighlighted,
      isPremium,
    };
  });
};

/**
 * Converts unified form data to V2 base vehicle API payload
 * This handles the common fields shared across all vehicle types
 *
 * @param formData - The form data to convert
 * @param existingFeatures - Existing vehicle features with current highlight/premium status
 * @param sourcingData - Feature data from sourcing API containing highlighted and premium feature arrays
 * @param existingVehicleData - Existing(previous) vehicle data for reference purposes - currently
 * this is only needed because of customFields, where updating behaves like PATCH
 */
// eslint-disable-next-line complexity -- Pre-existing violation
export const toAPIVehicle = (
  formData: BaseVehicle | SaleVehicle,
  existingFeatures?: VehicleFeatureV2ResponseBody[] | null,
  sourcingData?: CarFeaturesResponseBody | null,
  existingVehicleData?: GetVehicleApiResponse
): UpdateVehicleRequestBody => {
  const isElectricOrHybrid = isElectricOrHybridFuelType(formData.fuelType?.at(0));
  const make = getMakeValue(formData);
  const modelFamilyFields = toApiModelFamilyFields({
    make,
    modelFamily: formData.modelFamily,
    formModelFamily: formData.formModelFamily,
    customModelFamily: formData.customModelFamily,
  });

  return {
    ...omit(
      [
        'unknownMakes',
        'unknownModels',
        'formMake',
        'formModelFamily',
        'additionalNote',
        'auditData',
        'auditAssets',
        'sessionId',
        'batteryCertificateDocument',
        // T20-123004: modelRange persists via customFields, not as a top-level Vehicle property.
        'modelRange',
        'isFiscalPowerManualOverride',
        'isQualitySealManualOverride',
        'qualitySealKey',
        // Belongs to the service vehicle - it is written through `service` on create and
        // patchServiceVehicle on edit, never as a property of the vehicle itself.
        'vehicleClassId',
        // Posted per row to /vehicle/{id}/warranty once the vehicle exists.
        'warranties',
      ],
      formData
    ),
    // Basic Information

    axleCount: formData.axleCount?.at(0) ? Number(formData.axleCount.at(0)) : null,
    registrationPlate: formData.registrationPlate?.toUpperCase() || null,
    vin: formData.vin?.toUpperCase() || null,
    type:
      (formData.type?.at(0) as VehicleTypeEnum) || VehicleTypeEnumObject.VEHICLETYPE_PASSENGER_CAR,

    make,
    customMake:
      formData.customMake && formData.formMake && formData.customMake === formData.formMake
        ? formData.customMake
        : null,
    ...modelFamilyFields,

    // German-specific fields as customFields
    customFields: createCustomFields(formData, existingVehicleData),

    // Vehicle Details
    trim: formData.trim || null,
    variant: formData.variant || null,
    title: formData.title?.trim() || null,
    firstRegistrationOn: formData.firstRegistrationOn || null,

    // Consumption & Emissions
    // NEDC consumption fields
    fuelConsumptionCombined: isNotNil(formData.fuelConsumptionCombined)
      ? String(formData.fuelConsumptionCombined)
      : null,
    fuelConsumptionExtraUrban: isNotNil(formData.fuelConsumptionExtraUrban)
      ? String(formData.fuelConsumptionExtraUrban)
      : null,
    fuelConsumptionUrban: isNotNil(formData.fuelConsumptionUrban)
      ? String(formData.fuelConsumptionUrban)
      : null,
    // WLTP consumption fields (liquid/gas)
    fuelConsumptionWltpCombined: isNotNil(formData.fuelConsumptionWltpCombined)
      ? String(formData.fuelConsumptionWltpCombined)
      : null,
    fuelConsumptionWltpCityCenter: isNotNil(formData.fuelConsumptionWltpCityCenter)
      ? String(formData.fuelConsumptionWltpCityCenter)
      : null,
    fuelConsumptionWltpOutskirts: isNotNil(formData.fuelConsumptionWltpOutskirts)
      ? String(formData.fuelConsumptionWltpOutskirts)
      : null,
    fuelConsumptionWltpCountryRoads: isNotNil(formData.fuelConsumptionWltpCountryRoads)
      ? String(formData.fuelConsumptionWltpCountryRoads)
      : null,
    fuelConsumptionWltpHighway: isNotNil(formData.fuelConsumptionWltpHighway)
      ? String(formData.fuelConsumptionWltpHighway)
      : null,
    fuelConsumptionWltpWeighted: isNotNil(formData.fuelConsumptionWltpWeighted)
      ? String(formData.fuelConsumptionWltpWeighted)
      : null,
    // WLTP consumption fields (electric)
    fuelConsumptionWltpCombinedKwh: isNotNil(formData.fuelConsumptionWltpCombinedKwh)
      ? String(formData.fuelConsumptionWltpCombinedKwh)
      : null,
    fuelConsumptionWltpCityCenterKwh: isNotNil(formData.fuelConsumptionWltpCityCenterKwh)
      ? String(formData.fuelConsumptionWltpCityCenterKwh)
      : null,
    fuelConsumptionWltpOutskirtsKwh: isNotNil(formData.fuelConsumptionWltpOutskirtsKwh)
      ? String(formData.fuelConsumptionWltpOutskirtsKwh)
      : null,
    fuelConsumptionWltpCountryRoadsKwh: isNotNil(formData.fuelConsumptionWltpCountryRoadsKwh)
      ? String(formData.fuelConsumptionWltpCountryRoadsKwh)
      : null,
    fuelConsumptionWltpHighwayKwh: isNotNil(formData.fuelConsumptionWltpHighwayKwh)
      ? String(formData.fuelConsumptionWltpHighwayKwh)
      : null,
    fuelConsumptionWltpWeightedCombinedKwh: isNotNil(
      formData.fuelConsumptionWltpWeightedCombinedKwh
    )
      ? String(formData.fuelConsumptionWltpWeightedCombinedKwh)
      : null,
    carbonDioxideEmission: isNotNil(formData.carbonDioxideEmission)
      ? String(formData.carbonDioxideEmission)
      : null,
    carbonDioxideEmissionDischarged: isNotNil(formData.carbonDioxideEmissionDischarged)
      ? String(formData.carbonDioxideEmissionDischarged)
      : null,
    carbonDioxideEmissionWeightedCombined: isNotNil(formData.carbonDioxideEmissionWeightedCombined)
      ? String(formData.carbonDioxideEmissionWeightedCombined)
      : null,
    emissionClass: formData.emissionClass || null,
    carbonDioxideEmissionClass: formData.carbonDioxideEmissionClass || null,
    carbonDioxideEmissionClassDischarged: formData.carbonDioxideEmissionClassDischarged || null,
    carbonDioxideEmissionClassWeightedCombined:
      formData.carbonDioxideEmissionClassWeightedCombined || null,

    // Manufactured date - convert from separate year/month to ISO date
    manufacturedOn: formatManufacturedDate(
      formData.manufacturedOnYear,
      formData.manufacturedOnMonth
    ),

    // Technical Specs - convert arrays to single values
    bodyStyle: formData.bodyStyle?.at(0) || null,
    doorCountCategory: formData.doorCountCategory?.at(0) || null,
    seatCount: resolveApiSeatCount(formData.type?.at(0), formData.seatCount?.at(0)),
    fuelType: formData.fuelType?.at(0) || null,
    secondaryFuelType: formData.secondaryFuelType?.at(0) || null,
    hybridType: formData.hybridType?.at(0) || null,
    transmission: formData.transmission?.at(0) || null,
    drive: formData.drive?.at(0) || null,
    handDriveType: formData.handDriveType?.at(0) || null,
    dimensions: formData.dimensions
      ? {
          width: isNotNil(formData.dimensions.width) ? String(formData.dimensions.width) : null,
          height: isNotNil(formData.dimensions.height) ? String(formData.dimensions.height) : null,
          length: isNotNil(formData.dimensions.length) ? String(formData.dimensions.length) : null,
        }
      : null,
    dimensionsLoadingSpace: formData.dimensionsLoadingSpace
      ? {
          width: isNotNil(formData.dimensionsLoadingSpace.width)
            ? String(formData.dimensionsLoadingSpace.width)
            : null,
          height: isNotNil(formData.dimensionsLoadingSpace.height)
            ? String(formData.dimensionsLoadingSpace.height)
            : null,
          length: isNotNil(formData.dimensionsLoadingSpace.length)
            ? String(formData.dimensionsLoadingSpace.length)
            : null,
        }
      : null,
    weight: isNotNil(formData.weight) ? Number(formData.weight) : null,
    operatingWeight: isNotNil(formData.operatingWeight) ? Number(formData.operatingWeight) : null,
    loadingCapacity: isNotNil(formData.loadingCapacity) ? Number(formData.loadingCapacity) : null,
    trunkSpace: isNotNil(formData.trunkSpace) ? Number(formData.trunkSpace) : null,
    wheelBase: isNotNil(formData.wheelBase) ? Number(formData.wheelBase) : null,

    // Engine
    power: isNotNil(formData.power) ? Number(formData.power) : null,
    powerCombustionEngine: isNotNil(formData.powerCombustionEngine)
      ? String(formData.powerCombustionEngine)
      : null,
    powerElectricEngine: isNotNil(formData.powerElectricEngine)
      ? String(formData.powerElectricEngine)
      : null,
    engineCode: formData.engineCode || null,
    engineName: formData.engineName || null,
    engineNumber: formData.engineNumber || null,
    engineVolume: isNotNil(formData.engineVolume) ? Number(formData.engineVolume) : null,
    fiscalPower: isNotNil(formData.fiscalPower) ? Number(formData.fiscalPower) : null,
    isFiscalPowerManualOverride: formData.isFiscalPowerManualOverride ?? false,
    maxTorque: formData.maxTorque ? String(formData.maxTorque) : null,
    electricRange: isNotNil(formData.electricRange) ? Number(formData.electricRange) : null,

    // Electric Vehicle Battery - structured based on fuel type
    electricVehicleBattery: isElectricOrHybrid
      ? {
          chargingAC: formData.electricVehicleBattery?.chargingAC
            ? String(formData.electricVehicleBattery.chargingAC)
            : null,
          chargingDC: formData.electricVehicleBattery?.chargingDC
            ? String(formData.electricVehicleBattery.chargingDC)
            : null,
          batteryCapacityWh: formData.electricVehicleBattery?.batteryCapacityWh
            ? String(formData.electricVehicleBattery.batteryCapacityWh)
            : null,
          batteryCapacityAh: formData.electricVehicleBattery?.batteryCapacityAh
            ? String(formData.electricVehicleBattery.batteryCapacityAh)
            : null,
          batteryVoltage: formData.electricVehicleBattery?.batteryVoltage
            ? String(formData.electricVehicleBattery.batteryVoltage)
            : null,
        }
      : null,

    // Legacy integer-only mirror fields. For electric/hybrid vehicles the nested
    // `electricVehicleBattery` object is the single source of truth: it is the only
    // representation bound to a form input, it supports decimals, and the backend mirrors it
    // back into these top-level fields on read. They must therefore ALWAYS be null for
    // electric/hybrid vehicles — otherwise a value that is loaded into form state but is never
    // editable (no bound input) is silently re-sent on save and can never be cleared
    // (e.g. clearing "battery capacity in Ah" reappears on the offer). See T20-87236.
    // Non-electric vehicles keep using the top-level form fields.
    batteryCapacityWh: isElectricOrHybrid // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? null
      : isNotNil(formData.batteryCapacityWh)
        ? Number(formData.batteryCapacityWh)
        : null,
    batteryCapacityAh: isElectricOrHybrid // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? null
      : isNotNil(formData.batteryCapacityAh)
        ? Number(formData.batteryCapacityAh)
        : null,
    batteryVoltage: isElectricOrHybrid // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? null
      : isNotNil(formData.batteryVoltage)
        ? Number(formData.batteryVoltage)
        : null,
    batteryCount: isNotNil(formData.batteryCount) ? Number(formData.batteryCount) : null,

    // Legacy integer-only mirror fields (see battery note above): always null for
    // electric/hybrid vehicles (sourced from `electricVehicleBattery`); top-level form fields
    // for other vehicles. See T20-87236.
    chargingAC: isElectricOrHybrid // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? null
      : isNotNil(formData.chargingAC)
        ? Number(formData.chargingAC)
        : null,
    chargingDC: isElectricOrHybrid // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? null
      : isNotNil(formData.chargingDC)
        ? Number(formData.chargingDC)
        : null,

    // Other electric/battery related fields
    chargingTimeAC: isNotNil(formData.chargingTimeAC) ? Number(formData.chargingTimeAC) : null,
    chargingTimeDC: isNotNil(formData.chargingTimeDC)
      ? Math.round(Number(formData.chargingTimeDC))
      : null,
    batteryType: formData.batteryType || null,
    batteryMileageLimit: isNotNil(formData.batteryMileageLimit)
      ? Number(formData.batteryMileageLimit)
      : null,
    batteryWarrantyUntil: formData.batteryWarrantyUntil || null,
    certificateFileId: formData.batteryCertificateDocument?.fileId ?? null,
    batteryOwnershipType: formData.batteryOwnershipType || null,
    chargingConnectorType: formData.chargingConnectorType || null,
    hasRangeExtender: formData.hasRangeExtender ?? null,
    gasRevisionValidUntil: formData.gasRevisionValidUntil || null,
    pressureVesselValidUntil: formData.pressureVesselValidUntil || null,

    // Additional missing integer fields from schema
    cylinderCount: isNotNil(formData.cylinderCount) ? Number(formData.cylinderCount) : null,
    gearCount: isNotNil(formData.gearCount) ? Number(formData.gearCount) : null,
    ownerCount: isNotNil(formData.ownerCount) ? Number(formData.ownerCount) : null,
    primaryKeyCount: isNotNil(formData.primaryKeyCount) ? Number(formData.primaryKeyCount) : null,
    maximalPower: isNotNil(formData.maximalPower) ? Number(formData.maximalPower) : null,

    // Appearance
    exteriorColorSpecification: formData.exteriorColorSpecification?.at(0) || null,
    exteriorColor: formatColor(formData.exteriorColor),
    manufacturerColorCode: formData.manufacturerColorCode || null,
    manufacturerColorName: formData.manufacturerColorName || null,
    interiorMaterial: formData.interiorMaterial?.at(0) || null,
    interiorColor: formatColor(formData.interiorColor),
    interiorColorName: formData.interiorColorName || null,
    interiorColorCode: formData.interiorColorCode || null,

    // Service Book
    serviceBookType: formData.serviceBookType?.at(0) || null,
    serviceBookState: formData.serviceBookState?.at(0) || null,

    // Serviced in authorized network
    servicedInAuthorizedNetwork: match(formData.servicedInAuthorizedNetwork?.at(0))
      .with('yes', () => true)
      .with('no', () => false)
      .otherwise(() => null),

    // Technical Inspection
    isAcceptableForTechnicalInspection: formData.isAcceptableForTechnicalInspection ?? null,
    technicalInspectionNote: formData.technicalInspectionNote || null,
    technicalInspectionValidUntil: formData.technicalInspectionValidUntil || null,

    // Service Contract
    serviceContractMileageLimit: formData.serviceContractMileageLimit || null,
    serviceContractNote: formData.serviceContractNote || null,
    serviceContractUntil: formData.serviceContractUntil || null,
    warrantyDate: formData.warrantyDate || null,
    warrantyMileage: formData.warrantyMileage || null,

    // Features - properly implement highlighting and premium detection
    features: mapFeatures(formData.features || undefined, existingFeatures, sourcingData),

    // Condition
    condition: (formData.condition?.at(0) as ConditionEnum) || null,
  };
};

// TODO-SF: I think we might have platform util for this?
/**
 * Helper function to format manufactured date from year/month to ISO string
 */
const formatManufacturedDate = (year?: string | null, month?: string | null): string | null => {
  if (!year) {
    return null;
  }

  const yearNum = parseInt(year, 10);
  if (isNaN(yearNum) || yearNum < 1900) {
    return null;
  }

  if (!month) {
    return year;
  }

  const monthNum = parseInt(month, 10);
  if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
    return year;
  }

  return `${yearNum}-${month.toString().padStart(2, '0')}`;
};
